/**
 * API Service
 * Simple, direct API calls to Azure Functions backend.
 * All requests include Bearer token + x-tenant-id via authFetch.
 */

import { authFetch, API_BASE } from './authFetch';
import { OutputSubmissionStatus } from '../types';

export interface DocumentResponse {
  id: string;
  documentId: string;
  tenantId: string;
  documentType: string;
  originalFileName: string;
  blobStorageUrl: string;
  uploadedAt: string;
  uploadedBy: string;
  currentStatus: string;
  processingHistory: Array<{
    timestamp: string;
    stage: string;
    status: 'STARTED' | 'COMPLETED' | 'FAILED' | 'SKIPPED';
    duration?: number;
    details?: string;
    errorMessage?: string;
  }>;
  workflowVersion: string;
  aiExtraction: {
    extractedAt: string;
    modelVersion: string;
    confidence: { overall: number; byField: Record<string, number> };
    fields: Array<{
      id: string;
      name: string;
      value: string;
      confidence: number;
      fieldType: 'text' | 'number' | 'date' | 'currency' | 'boolean';
      required: boolean;
      boundingBoxes: Array<{ page: number; x: number; y: number; width: number; height: number }>;
    }>;
  };
  ownershipInference: {
    inferredAt: string;
    ownershipType: string;
    confidence: number;
    requiresHumanReview: boolean;
    processingTimeMs: number;
  };
  validation: {
    required: boolean;
    assignedTo?: string;
    completedAt?: string;
    validatedBy?: string;
    corrections: Array<{
      fieldId: string;
      originalValue: string;
      correctedValue: string;
      correctionReason?: string;
      timestamp: string;
      correctedBy: string;
    }>;
    reviewNotes?: string;
    validationDuration?: number;
  };
  submission: {
    submittedBy: string;
    submittedAt?: string;
    retryAttempts: number;
    lastRetryAt?: string;
    augmentumResponse?: {
      status: 'SUCCESS' | 'FAILURE' | 'PARTIAL';
      responseCode: string;
      responseMessage: string;
      transactionId?: string;
    };
    /** Per-output delivery results (Packet E2/E3) — absent on documents submitted before this. */
    outputs?: OutputSubmissionStatus[];
    /** Output ids still awaiting their scheduled batch export. */
    pendingOutputs?: string[];
  };
  auditTrail: Array<{
    timestamp: string;
    action: string;
    performedBy: string;
    details: Record<string, unknown>;
    ipAddress?: string;
    userAgent?: string;
  }>;
}

/** Partial update payload for PATCH /documents/:id */
export interface DocumentUpload {
  currentStatus?: string;
  originalFileName?: string;
  documentType?: string;
  validation?: {
    required?: boolean;
    assignedTo?: string;
    completedAt?: string;
    validatedBy?: string;
    corrections?: Array<{
      fieldId: string;
      originalValue: string;
      correctedValue: string;
      correctionReason?: string;
      timestamp: string;
      correctedBy: string;
    }>;
    reviewNotes?: string;
    validationDuration?: number;
  };
  submission?: {
    submittedBy?: string;
    submittedAt?: string;
    retryAttempts?: number;
    lastRetryAt?: string;
    augmentumResponse?: {
      status: 'SUCCESS' | 'FAILURE' | 'PARTIAL';
      responseCode: string;
      responseMessage: string;
      transactionId?: string;
    };
  };
}

/** Request body for POST/PUT /configuration — flat key-value pairs to update */
export interface ConfigurationResponse {
  updates: Record<string, string | number | boolean | null>;
}

export class ApiService {
  private static instance: ApiService;

  private constructor() {}

  public static getInstance(): ApiService {
    if (!ApiService.instance) {
      ApiService.instance = new ApiService();
    }
    return ApiService.instance;
  }

  // Document operations
  async getDocuments() {
    const response = await authFetch('/documents');
    if (!response.ok) throw new Error(`Failed to fetch documents: ${response.statusText}`);
    return await response.json();
  }

  async getDocument(id: string) {
    const response = await authFetch(`/documents/${id}`);
    if (!response.ok) throw new Error(`Failed to fetch document: ${response.statusText}`);
    return await response.json();
  }

  async uploadDocument(file: File) {
    const formData = new FormData();
    formData.append('file', file);
    // For FormData, let the browser set Content-Type (with boundary)
    const response = await authFetch('/documents/upload', {
      method: 'POST',
      body: formData,
      headers: {} // override default Content-Type so browser sets multipart boundary
    });
    if (!response.ok) throw new Error(`Failed to upload document: ${response.statusText}`);
    return await response.json();
  }

  async updateDocument(id: string, updates: DocumentUpload) {
    const response = await authFetch(`/documents/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(updates)
    });
    if (!response.ok) throw new Error(`Failed to update document: ${response.statusText}`);
    return await response.json();
  }

  async deleteDocument(id: string) {
    const response = await authFetch(`/documents/${id}`, { method: 'DELETE' });
    if (!response.ok) throw new Error(`Failed to delete document: ${response.statusText}`);
    return await response.json();
  }

  async updateDocumentStatus(id: string, status: string) {
    const response = await authFetch(`/documents/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status })
    });
    if (!response.ok) throw new Error(`Failed to update document status: ${response.statusText}`);
    return await response.json();
  }

  async getNextProcessedDocument(documentType?: string) {
    // Keep the analyst on the same document model when a type is supplied (e.g. only Deeds after a Deed).
    const query = documentType ? `?documentType=${encodeURIComponent(documentType)}` : '';
    const response = await authFetch(`/queue/next-processed${query}`);
    if (!response.ok) throw new Error(`Failed to get next document: ${response.statusText}`);
    return await response.json();
  }

  /** Deliver a single pipeline output (HITL per-output submit). Throws on API-delivery failure. */
  async submitOutput(documentId: string, outputId: string) {
    const response = await authFetch(`/documents/${documentId}/submit-output`, {
      method: 'POST',
      body: JSON.stringify({ outputId })
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.error || `Failed to submit output: HTTP ${response.status}`);
    }
    return await response.json();
  }

  /**
   * Fetch the next document from a specific exception group (failed / stranded / on-hold).
   * Returns the exception dashboard data so SuccessStage can pick the next item from the
   * same group the analyst was working through, not the general PENDING_VALIDATION queue.
   */
  async getExceptionDashboard() {
    const response = await authFetch('/dashboard/exceptions');
    if (!response.ok) throw new Error(`Failed to fetch exception dashboard: ${response.statusText}`);
    return await response.json();
  }

  // Configuration operations
  async getConfiguration() {
    const response = await authFetch('/configuration');
    if (!response.ok) throw new Error(`Failed to fetch configuration: ${response.statusText}`);
    return await response.json();
  }

  async updateConfiguration(updates: ConfigurationResponse) {
    const response = await authFetch('/configuration', {
      method: 'PATCH',
      body: JSON.stringify(updates)
    });
    if (!response.ok) throw new Error(`Failed to update configuration: ${response.statusText}`);
    return await response.json();
  }

  // Health check (no auth needed — exempt in middleware)
  async healthCheck() {
    try {
      const response = await fetch(`${API_BASE}/health`);
      return { status: response.ok ? 'healthy' : 'unhealthy', mode: 'production' };
    } catch (error) {
      return {
        status: 'unhealthy',
        mode: 'production',
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }
}

// Export singleton instance
export const apiService = ApiService.getInstance();
